/**
 * @puffin-core/feature-flags/react-http — `createFlagLastReadStore`
 *
 * Where a web app keeps its last good flag read (UAT-BLOCKERS D1 item 11, owner ruling 2026-10-22 "Remember the last
 * good read"): ONE record, `{ scope, readAt, flags }`, in the app's ONE `@puffin-core/storage` `StorageService`
 * (injected, as `createAuthTokenStorage` takes it; this package does not depend on it). That service already answers
 * blocked, private-mode and unparseable storage with `null`, so a store that cannot be read is simply "no good read
 * yet" and the reader falls back to the declared defaults. The app clears the record on sign-out through
 * `createAuthTokenStorage({ clearOnSignOut })`.
 *
 * @module @puffin-core/feature-flags/react-http/createFlagLastReadStore
 */

/** The `StorageService` namespace a flag read is kept under. */
export const FLAG_LAST_READ_NAMESPACE = 'featureFlags';

/**
 * @param {Object} options
 * @param {{getNamespace: Function}} options.storage - The web app's ONE `StorageService` instance. REQUIRED.
 * @param {string} [options.key='lastRead'] - The record's key; a second reader on the same origin (purchase-web's
 *   storefront) names its own.
 * @returns {Readonly<{read: () => *, write: (record: Object) => void, clear: () => void}>}
 * @throws {TypeError} when `storage` is not a `StorageService`, or `key` is blank.
 */
export function createFlagLastReadStore(options) {
  const { storage, key = 'lastRead' } = options ?? {};
  if (typeof storage?.getNamespace !== 'function') {
    throw new TypeError(
      'createFlagLastReadStore needs `storage`: the web app’s ONE StorageService instance',
    );
  }
  if (typeof key !== 'string' || key.length === 0) {
    throw new TypeError('createFlagLastReadStore: `key` must be a non-empty string');
  }
  const namespace = () => storage.getNamespace(FLAG_LAST_READ_NAMESPACE);
  return Object.freeze({
    read: () => namespace().getJSON(key),
    write: (record) => namespace().setJSON(key, record),
    clear: () => namespace().remove(key),
  });
}
